import { useEffect, useState } from "react";
import { EmailSettingsType } from "../../types/EmailSetingsType";
import { useEmailSettings } from "../../hooks/useEmailSettings";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { createEmailSettings, deleteEmailSettings, updateEmailSettings } from "../../services/user/EmailSettings";
import toast from "react-hot-toast";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { Button, Card, Flex, IconButton, Spinner, Tooltip } from "@radix-ui/themes";
import { CheckIcon, EyeClosedIcon, EyeOpenIcon, TrashIcon } from "@radix-ui/react-icons";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import EmailSettingsFormValidation from "../Courses/EmailSetingsFormValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import { key_EmailSetings } from "../../utils/constants";

/**
 * Os `defaultValues` são lidos uma única vez, na primeira renderização — altura
 * em que a query ainda está em curso. O preenchimento vem sempre do `useEffect`.
 */
const EMPTY_VALUES = {
  sendHost: "",
  sendPort: "",
  sendFrom: "",
  sendUsername: "",
  sendPassword: "",
  receiveHost: "",
  receivePort: "",
  receiveFrom: "",
  receiveUsername: "",
  receivePassword: "",
} as unknown as EmailSettingsType;

const EmailSettings = () => {
  const [showSendPassword, setShowSendPassword] = useState(false);
  const [showReceivePassword, setShowReceivePassword] = useState(false);

  const { data: dataEmail, isLoading: isLoadingEmail } = useEmailSettings({
    enabled: true,
    refetchOnWindowFocus: false,
  });
  const queryClient = useQueryClient();

  const [updateEmail, setUpdateEmail] = useState<EmailSettingsType | null>(null);
  const [saveEmail, setSaveEmail] = useState<EmailSettingsType | null>(null);
  const [removeEmail, setRemoveEmail] = useState<EmailSettingsType | null>(null);

  const mutationCreate = useMutation({
    mutationFn: createEmailSettings,
    mutationKey: "createEmailSettings",
  });
  const mutationUpdate = useMutation({
    mutationFn: updateEmailSettings,
    mutationKey: "updateEmailSettings",
  });
  const mutationDelete = useMutation({
    mutationFn: deleteEmailSettings,
    mutationKey: "deleteEmailSettings",
  });

  const isSubmitting =
    mutationCreate.isLoading || mutationUpdate.isLoading || mutationDelete.isLoading;

  const {
    control: controlEmail,
    handleSubmit: handleSubmitEmail,
    reset: resetEmail,
    getValues: getValuesEmail,
    formState: { errors: errorsEmail },
  } = useForm<EmailSettingsType>({
    resolver: yupResolver(EmailSettingsFormValidation),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!dataEmail?.id) return;
    // O GET pode não devolver as senhas; nesse caso mantêm-se as que já estão no
    // formulário, senão os campos esvaziavam-se logo após uma gravação.
    resetEmail({
      ...dataEmail,
      sendPassword: dataEmail.sendPassword || getValuesEmail("sendPassword") || "",
      receivePassword:
        dataEmail.receivePassword || getValuesEmail("receivePassword") || "",
    });
  }, [dataEmail, resetEmail, getValuesEmail]);

  const confirmSaveEmail = async () => {
    if (!saveEmail) return;
    try {
      await mutationCreate.mutateAsync(saveEmail);
      // Repõe com os valores submetidos — o `reset()` sem argumentos voltaria
      // aos `defaultValues` vazios e limpava o formulário à vista do utilizador.
      resetEmail(saveEmail);
      toast.success("Configuração de Email guardada com sucesso!");
      await queryClient.invalidateQueries(key_EmailSetings);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao guardar a configuração de Email");
    } finally {
      setSaveEmail(null);
    }
  };

  const confirmUpdateEmail = async () => {
    if (!updateEmail) return;
    try {
      await mutationUpdate.mutateAsync(updateEmail);
      resetEmail(updateEmail);
      toast.success("Configuração de Email actualizada!");
      await queryClient.invalidateQueries(key_EmailSetings);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao actualizar a configuração de Email");
    } finally {
      setUpdateEmail(null);
    }
  };

  const confirmRemoveEmail = async () => {
    if (!removeEmail?.id) return;
    try {
      await mutationDelete.mutateAsync(removeEmail.id);
      resetEmail(EMPTY_VALUES);
      toast.success("Configuração removida!");
      // `removeQueries` e não `invalidateQueries`: se o GET seguinte falhar (o
      // registo já não existe), o React Query mantinha o `data` anterior em
      // cache e os botões continuariam a mostrar "Actualizar"/"Remover".
      queryClient.removeQueries(key_EmailSetings);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao remover a configuração de Email");
    } finally {
      setRemoveEmail(null);
    }
  };

  const handleEmailSubmit = (values: EmailSettingsType) => {
    if (dataEmail?.id) {
      setUpdateEmail({ ...values, id: dataEmail.id });
    } else {
      setSaveEmail(values);
    }
  };

  if (isLoadingEmail) {
    return (
      <Flex align="center" justify="center" p="5">
        <Spinner loading />
      </Flex>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmitEmail(handleEmailSubmit)} noValidate>
        <Flex direction="row" gap="4" mt="4" wrap="wrap">
          <Card size="3" style={{ flex: 1, minWidth: 320 }}>
            <Flex direction="column" gap="3">
              <TextFieldComponent
                name="sendHost"
                label="Servidor de Envio"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ placeholder: "ex: smtp.gmail.com" }}
              />

              <TextFieldComponent
                name="sendPort"
                label="Porta de Envio"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ type: "number", placeholder: "587" }}
              />

              <TextFieldComponent
                name="sendFrom"
                label="Email Remetente"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ type: "email", placeholder: "seu@email.com" }}
              />

              <TextFieldComponent
                name="sendUsername"
                label="Utilizador de Envio"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ placeholder: "utilizador SMTP" }}
              />

              <div style={{ position: "relative", width: "100%" }}>
                <TextFieldComponent
                  name="sendPassword"
                  label="Senha de Envio"
                  control={controlEmail}
                  errors={errorsEmail}
                  fieldProps={{
                    type: showSendPassword ? "text" : "password",
                    placeholder: "********",
                  }}
                />
                <Tooltip content={showSendPassword ? "Ocultar senha" : "Mostrar senha"}>
                  <IconButton
                    type="button"
                    variant="ghost"
                    color="gray"
                    aria-label={showSendPassword ? "Ocultar senha de envio" : "Mostrar senha de envio"}
                    onClick={() => setShowSendPassword((p) => !p)}
                    style={{
                      position: "absolute",
                      right: "var(--space-1)",
                      top: "var(--space-5)",
                    }}
                  >
                    {showSendPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </IconButton>
                </Tooltip>
              </div>
            </Flex>
          </Card>

          <Card size="3" style={{ flex: 1, minWidth: 320 }}>
            <Flex direction="column" gap="3">
              <TextFieldComponent
                name="receiveHost"
                label="Servidor de Recebimento"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ placeholder: "imap.gmail.com" }}
              />

              <TextFieldComponent
                name="receivePort"
                label="Porta de Recebimento"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ type: "number", placeholder: "993" }}
              />

              <TextFieldComponent
                name="receiveFrom"
                label="Email de Recebimento"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ type: "email", placeholder: "usuario@email.com" }}
              />

              <TextFieldComponent
                name="receiveUsername"
                label="Utilizador de Recepção"
                control={controlEmail}
                errors={errorsEmail}
                fieldProps={{ placeholder: "utilizador IMAP" }}
              />

              <div style={{ position: "relative", width: "100%" }}>
                <TextFieldComponent
                  name="receivePassword"
                  label="Senha de Recebimento"
                  control={controlEmail}
                  errors={errorsEmail}
                  fieldProps={{
                    type: showReceivePassword ? "text" : "password",
                    placeholder: "********",
                  }}
                />
                <Tooltip content={showReceivePassword ? "Ocultar senha" : "Mostrar senha"}>
                  <IconButton
                    type="button"
                    variant="ghost"
                    color="gray"
                    aria-label={showReceivePassword ? "Ocultar senha de recepção" : "Mostrar senha de recepção"}
                    onClick={() => setShowReceivePassword((p) => !p)}
                    style={{
                      position: "absolute",
                      right: "var(--space-1)",
                      top: "var(--space-5)",
                    }}
                  >
                    {showReceivePassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </IconButton>
                </Tooltip>
              </div>
            </Flex>
          </Card>
        </Flex>

        <Flex direction="row" gap="3" justify="end" mt="4">
          {dataEmail?.id && (
            <Button
              type="button"
              variant="soft"
              color="red"
              disabled={isSubmitting}
              onClick={() => setRemoveEmail(dataEmail)}
            >
              <TrashIcon /> Remover
            </Button>
          )}

          {/* Sem `onClick` a definir o alvo: o botão apenas submete. Antes fazia
              `setUpdateEmail(dataEmail)`, o que enviava os dados do servidor em
              vez dos editados e abria o diálogo mesmo com o formulário inválido. */}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Spinner loading>
                <CheckIcon />
              </Spinner>
            ) : (
              <CheckIcon />
            )}
            {dataEmail?.id ? "Actualizar" : "Guardar"}
          </Button>
        </Flex>
      </form>

      <ConfirmAlertDialog
        open={!!saveEmail}
        onOpenChange={(open) => !open && setSaveEmail(null)}
        title="Guardar configuração de Email"
        description="As definições de envio e de recepção de correio electrónico serão guardadas."
        confirmLabel="Guardar"
        confirmColor="green"
        disabledConfirm={isSubmitting}
        item={saveEmail}
        onConfirm={confirmSaveEmail}
        onCancel={() => setSaveEmail(null)}
      />

      <ConfirmAlertDialog
        open={!!updateEmail}
        onOpenChange={(open) => !open && setUpdateEmail(null)}
        title="Actualizar configuração de Email"
        description="As definições de correio electrónico existentes serão substituídas."
        confirmLabel="Actualizar"
        confirmColor="blue"
        disabledConfirm={isSubmitting}
        item={updateEmail}
        onConfirm={confirmUpdateEmail}
        onCancel={() => setUpdateEmail(null)}
      />

      <ConfirmAlertDialog
        open={!!removeEmail}
        onOpenChange={(open) => !open && setRemoveEmail(null)}
        title="Remover configuração de Email"
        description="A instituição deixará de conseguir enviar e receber correio electrónico. Pretende continuar?"
        confirmLabel="Remover"
        disabledConfirm={isSubmitting}
        item={removeEmail}
        onConfirm={confirmRemoveEmail}
        onCancel={() => setRemoveEmail(null)}
      />
    </>
  );
};
export default EmailSettings;
